import { AccountState } from '@/services/auth/types';
import { isEmptyString } from '@/utils/string';

/** The billing state the payment-issue banner reads. */
export type PaymentIssueFacts = {
  accountState: string | undefined;
  /** The subscription is billed by Ordway, which reads its payment state from the Ordway customer. */
  isOrdwaySubscription: boolean;
  isSubscriptionActive: boolean;
  isPendingCheckout: boolean;
  isLumpSumPlan: boolean;
  hasAchSetup: boolean;
  hasCard: boolean;
  hasFailedOverageCharge: boolean;
  lastPaymentError: string | null;
  /** Unix seconds. Undefined for an account that Ordway or Metronome bills. */
  pinnedStartDate: number | undefined;
  hasPinnedPlan: boolean;
  startDateInFuture: boolean;
  canViewBilling: boolean;
};

/** Which payment problem the banner states. Each kind has its own title, link and copy. */
export type PaymentIssueAlert =
  | { kind: 'payment-error' }
  | { kind: 'annual-subscription' }
  | { kind: 'overages-failed'; hasCard: boolean }
  | { kind: 'payment-failed'; hasCard: boolean }
  | { kind: 'start-date-passed' }
  | { kind: 'no-active-subscription' }
  | { kind: 'ask-an-admin' };

function isOverdueOrPaymentPending(facts: PaymentIssueFacts): boolean {
  return facts.accountState === AccountState.Overdue || facts.accountState === AccountState.PaymentPending;
}

// `pinnedStartDate !== 0` holds for an undefined date too. Ember compares the same way.
function hasPinnedStartDate(facts: PaymentIssueFacts): boolean {
  return facts.pinnedStartDate !== 0;
}

/** Whether the account has a payment problem at all. */
export function hasPaymentIssue(facts: PaymentIssueFacts): boolean {
  if (isOverdueOrPaymentPending(facts)) {
    return true;
  }
  if (hasPinnedStartDate(facts) && facts.hasPinnedPlan && !facts.isSubscriptionActive) {
    return true;
  }
  if (facts.isLumpSumPlan) {
    return false;
  }
  return facts.isSubscriptionActive && facts.hasFailedOverageCharge;
}

function hasActiveSubscription(facts: PaymentIssueFacts): boolean {
  if (facts.isOrdwaySubscription) {
    return facts.isSubscriptionActive || facts.isPendingCheckout;
  }
  return facts.isSubscriptionActive;
}

function isLumpSumSubscription(facts: PaymentIssueFacts): boolean {
  return facts.isLumpSumPlan || facts.hasAchSetup;
}

function isOveragesOnly(facts: PaymentIssueFacts): boolean {
  return facts.isLumpSumPlan && facts.hasFailedOverageCharge;
}

function hasPaymentMethod(facts: PaymentIssueFacts): boolean {
  return facts.hasAchSetup || facts.hasCard;
}

function isAnnualSubscriptionIssue(facts: PaymentIssueFacts): boolean {
  return hasActiveSubscription(facts) && isLumpSumSubscription(facts) && !facts.hasFailedOverageCharge;
}

function isOveragesFailed(facts: PaymentIssueFacts): boolean {
  return hasActiveSubscription(facts) && isOveragesOnly(facts);
}

function isPaymentFailed(facts: PaymentIssueFacts): boolean {
  if (facts.isOrdwaySubscription) {
    return !isEmptyString(facts.lastPaymentError) && hasPaymentMethod(facts);
  }
  return hasActiveSubscription(facts) && !isLumpSumSubscription(facts) && !isOveragesOnly(facts);
}

// For Ordway this reads a start date in the future, which is what Ember does.
function isStartDatePassedWithoutPayment(facts: PaymentIssueFacts): boolean {
  if (facts.isOrdwaySubscription) {
    return facts.startDateInFuture && !hasPaymentMethod(facts);
  }
  return (
    hasPinnedStartDate(facts) &&
    facts.pinnedStartDate != null &&
    !facts.startDateInFuture &&
    !hasActiveSubscription(facts)
  );
}

function isWithoutActiveSubscription(facts: PaymentIssueFacts): boolean {
  if (facts.isOrdwaySubscription) {
    return !facts.startDateInFuture && !hasPaymentMethod(facts);
  }
  return !hasActiveSubscription(facts) && hasPinnedStartDate(facts) && facts.hasPinnedPlan;
}

function billingAdminAlert(facts: PaymentIssueFacts): PaymentIssueAlert {
  if (isAnnualSubscriptionIssue(facts)) {
    return { kind: 'annual-subscription' };
  }
  if (isOveragesFailed(facts)) {
    return { kind: 'overages-failed', hasCard: facts.hasCard };
  }
  if (isPaymentFailed(facts)) {
    return { kind: 'payment-failed', hasCard: facts.hasCard };
  }
  if (isStartDatePassedWithoutPayment(facts)) {
    return { kind: 'start-date-passed' };
  }
  if (isWithoutActiveSubscription(facts)) {
    return { kind: 'no-active-subscription' };
  }
  return { kind: 'payment-error' };
}

/**
 * The payment problem to state, or null when there is none to state to this viewer.
 *
 * A viewer who can see billing gets the problem and a link to fix it. Anyone else hears only that an
 * overdue account has a problem, and to ask an admin.
 */
export function paymentIssueAlert(facts: PaymentIssueFacts): PaymentIssueAlert | null {
  if (!hasPaymentIssue(facts)) {
    return null;
  }
  if (facts.canViewBilling) {
    return billingAdminAlert(facts);
  }
  if (facts.accountState === AccountState.Overdue) {
    return { kind: 'ask-an-admin' };
  }
  return null;
}

/** Where each alert's "Click here" link goes. */
export type PaymentIssuePaths = {
  /** The tab that holds the card on file. */
  cardTab: string;
  billingHistory: string;
  checkout: string;
  /** An Ordway account adds its card on the card tab. Any other account does it in checkout. */
  isOrdwayCustomer: boolean;
};

/** The page the alert links to. The ask-an-admin alert links to none. */
export function paymentIssueHref(alert: PaymentIssueAlert, paths: PaymentIssuePaths): string | undefined {
  switch (alert.kind) {
    case 'payment-error':
    case 'overages-failed':
    case 'payment-failed':
      return paths.cardTab;
    case 'annual-subscription':
      return paths.billingHistory;
    case 'start-date-passed':
    case 'no-active-subscription':
      return paths.isOrdwayCustomer ? paths.cardTab : paths.checkout;
    case 'ask-an-admin':
      return undefined;
    default: {
      const _exhaustive: never = alert;
      return String(_exhaustive);
    }
  }
}

/**
 * Whether the viewer is already where the problem gets fixed: on the page the alert links to, or in
 * checkout. The linked page has to match exactly, so the banner still shows on `/settings`.
 */
export function isOnPaymentFixPage(pathname: string, href: string | undefined, checkoutPath: string): boolean {
  return pathname.startsWith(checkoutPath) || pathname === href;
}

export function paymentIssueTitle(alert: PaymentIssueAlert): string {
  switch (alert.kind) {
    case 'payment-error':
      return 'There is a problem with payment for your subscription';
    case 'annual-subscription':
    case 'ask-an-admin':
      return 'There is a problem with payment for your account';
    case 'overages-failed':
      return 'Your overages payment failed';
    case 'payment-failed':
      return 'Your payment failed';
    case 'start-date-passed':
    case 'no-active-subscription':
      return 'Set up your payment method';
    default: {
      const _exhaustive: never = alert;
      return String(_exhaustive);
    }
  }
}
